/**
 * Search boxes that take MANY numbers at once (AWB, LR) — turn whatever separates them into commas
 * as the person types or pastes.
 *
 * A column copied out of Excel arrives one number per line; a list from WhatsApp or an email arrives
 * space-separated. Both used to sit in the box looking like one long value, and on the order screens
 * the server read a space-separated list as ONE number and found nothing. Rewriting the separators
 * to commas on the spot makes the list visible for what it is and matches what the box asks for.
 *
 *   "500641485866 21194912771753"      → "500641485866,21194912771753"
 *   "500641485866\n21194912771753\n"   → "500641485866,21194912771753,"
 *   "500641485866, 21194912771753"     → "500641485866,21194912771753"
 *
 * A trailing comma is left alone — the person is about to type the next number.
 */
export function toCommaList(value: string): string {
  return String(value ?? '')
    .replace(/[\s;|]+/g, ',')   // spaces, tabs, line breaks (and ; |) between numbers → comma
    .replace(/,{2,}/g, ',')     // "123, 456" became "123,,456" — one comma is enough
    .replace(/^,+/, '');        // never start with a comma
}

/**
 * For a box that searches identifiers AND names (the top bar: "AWB, order no, mobile / name"):
 * only when every word carries a digit — i.e. it is a list of numbers, not "Anand Kumar" — are the
 * spaces turned into commas. A name keeps its spaces.
 */
export function toCommaListIfNumbers(value: string): string {
  const words = String(value ?? '').split(/[\s,;|]+/).filter(Boolean);
  if (words.length === 0 || !words.every((w) => /\d/.test(w))) return value;
  return toCommaList(value);
}

/** How many numbers are in a comma list (blank entries ignored). */
export function commaListCount(value: string): number {
  return String(value ?? '').split(/[\s,;|]+/).filter(Boolean).length;
}
